import { Avatar, Empty, Select, Spin, Timeline } from 'antd';
import dayjs from 'dayjs';
import type ActivityLog from '../../../interfaces/ActivityLog';
import type File from '../../../interfaces/File';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityLogTargetTypeEnum } from '../../../enums/ActivityLogTargetTypeEnum';
import { ActivityLogOperationTypeEnum } from '../../../enums/ActivityLogOperationTypeEnum';
import type { GetActivityLogsPayload } from '../../../interfaces/ActivityLogApiService';
import { activityLogGroupedTargetOperationTypes } from '../../../lib/activityLogGroupedTargetOperationTypes';
import { ActivityLogService } from '../../../services/activityLogApiService';
import { getAvatarColor } from '../../../utils/avatarColors';
import { dateToTicks, dateFromTicks } from '../../../utils/utils';
import { Icon } from '../../Core/Icon';

interface FileDetailActivitiesTabProps {
  file: File;
}

const activityLogService = new ActivityLogService();
const SCROLL_THRESHOLD = 50;

const FileDetailActivitiesTab: React.FC<FileDetailActivitiesTabProps> = ({ file }) => {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<GetActivityLogsPayload>({
    offset: 0,
    count: 25,
    fromDate: 0,
    toDate: dateToTicks(new Date()),
    detailsContains: '',
    targetId: file.id,
    targetType: file.isFolder ? 'Folder' : 'File',
    operationType: undefined,
  });
  const [activities, setActivities] = useState<ActivityLog[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const containerRef = useRef<HTMLDivElement>(null);
  const isFirstMount = useRef(true);

  /**
   * @description Fetch activities
   */
  const fetchActivities = useCallback(async () => {
    setIsLoading(true);
    try {
      const resp = await activityLogService.getActivityLogs(filters);
      const newLogs = resp.activityLogs;
      setActivities((prev) => (filters.offset === 0 ? newLogs : [...prev, ...newLogs]));
      setHasMore(newLogs.length === filters.count);
    } catch (err) {
      console.error('Error fetching activities', err);
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  /**
   * @description Infinite scroll
   */
  const handleScroll = () => {
    const el = containerRef.current;
    if (
      !el ||
      isLoading ||
      !hasMore ||
      el.scrollTop + el.clientHeight < el.scrollHeight - SCROLL_THRESHOLD
    )
      return;

    setFilters((prev) => ({
      ...prev,
      offset: prev.offset + prev.count,
    }));
  };

  /**
   * @description Get file operation types
   */
  const fileOperationTypes = useMemo(() => {
    const fileItem = activityLogGroupedTargetOperationTypes.find(
      (item) => item.value === ActivityLogTargetTypeEnum.File.toString(),
    );

    if (!fileItem?.children) return [];

    return (
      fileItem?.children
        .filter((item) => item.value !== ActivityLogOperationTypeEnum.CreateFile.toString())
        .map((item) => ({
          label: t(item.title),
          value: item.value,
        })) ?? []
    );
  }, []);

  /**
   * @description Fetch activities on offset change (and on any change of filters after the reset)
   */
  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  /**
   * @description Reset state and filters when file.id changes (skip on first mount)
   */
  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }
    setActivities([]);
    setHasMore(true);
    setFilters({
      offset: 0,
      count: 25,
      fromDate: 0,
      toDate: dateToTicks(new Date()),
      detailsContains: '',
      targetId: file.id,
      targetType: file.isFolder ? 'Folder' : 'File',
    });
  }, [file.id]);

  /**
   * @description Group activities by date and create items for Timeline
   */
  const timelineItems = useMemo(() => {
    const groups: Record<string, ActivityLog[]> = {};
    activities?.forEach((act) => {
      const date = dateFromTicks(act.timestamp);
      const dateKey = dayjs(date).format('YYYY-MM-DD');
      if (!groups[dateKey]) groups[dateKey] = [];
      groups[dateKey].push(act);
    });

    const sortedDates = Object.keys(groups).sort((a, b) => dayjs(b).valueOf() - dayjs(a).valueOf());

    const items: any[] = [];
    sortedDates?.forEach((dateKey) => {
      // header date
      items.push({
        key: `header-${dateKey}`,
        children: (
          <span className="text-blue-dark w-full font-bold italic">
            {dayjs(dateKey).format('DD MMMM YYYY')}
          </span>
        ),
        dot: <Icon name="CalendarClockIcon" className="text-blue-dark w-6" />,
      });
      // activities of the day
      groups?.[dateKey]?.forEach((act, idx) => {
        const time = dayjs(dateFromTicks(act.timestamp)).format('HH:mm');
        const initials =
          act?.additionalData?.Name?.[0] ||
          act?.additionalData?.Surname?.[0] ||
          act?.additionalData?.Email?.[0] ||
          act?.additionalData?.UserName?.[0];
        const isSystemActor = act.userId == null || act.userId === 0;
        const isMalwareDetection =
          act.operationType === ActivityLogOperationTypeEnum.MalwareDetection;
        // Generate a consistent ID from email or name for color consistency
        const activityUserId = (() => {
          const email = act?.additionalData?.Email;
          const name = act?.additionalData?.Name || act?.additionalData?.UserName;
          const identifier = email || name || '';
          if (!identifier) return 0;
          // Simple hash function to convert string to number
          let hash = 0;
          for (let i = 0; i < identifier.length; i++) {
            const char = identifier.charCodeAt(i);
            hash = (hash << 5) - hash + char;
            hash = hash & hash; // Convert to 32-bit integer
          }
          return Math.abs(hash);
        })();

        const avatarContent = (() => {
          if (isSystemActor && isMalwareDetection) {
            // Same infected glyph as FileAntivirusStatusIcon (ShieldBan).
            return <Icon name="ShieldBanIcon" className="text-red-dark h-4 w-4" />;
          }
          if (isSystemActor && !initials) {
            return <Icon name="HatGlassesIcon" className="h-4 w-4" />;
          }
          return initials;
        })();

        items.push({
          key: `${dateKey}-${act.timestamp}-${idx}`,
          dot: (
            <Avatar
              size={26}
              className="text-blue-dark! shrink-0! uppercase!"
              style={{ backgroundColor: getAvatarColor(activityUserId) }}
            >
              {avatarContent}
            </Avatar>
          ),
          children: (
            <div>
              <div className="flex flex-row items-start gap-2">
                <div className="flex flex-col">
                  <span className="font-medium">
                    {isSystemActor && !act?.additionalData?.Name && !act?.additionalData?.Surname
                      ? t('common.systemUser')
                      : `${act?.additionalData?.Name ?? ''} ${act?.additionalData?.Surname ?? ''}`.trim()}
                  </span>
                  {act?.additionalData?.Email && (
                    <span className="text-gray-dark -mt-1 pl-0.5 text-xs">
                      {act?.additionalData?.Email}
                    </span>
                  )}
                </div>
                <span className="text-gray-dark pt-0.5 text-sm">- {time}</span>
              </div>
              <div>{act?.details}</div>
            </div>
          ),
        });
      });
    });

    return items;
  }, [activities, t]);

  return (
    <div className="flex h-full flex-col gap-4">
      <Select<string>
        loading={isLoading}
        options={fileOperationTypes}
        allowClear
        showSearch
        placeholder={t('common.operationType')}
        onChange={(value) => {
          setActivities([]);
          setHasMore(true);
          setFilters((prev) => ({
            ...prev,
            offset: 0,
            operationType: value as ActivityLogOperationTypeEnum | undefined,
          }));
        }}
        value={filters.operationType}
      />

      {!isLoading && timelineItems?.length === 0 && (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('common.noActivities')} />
      )}

      <div
        ref={containerRef}
        onScroll={handleScroll}
        className="h-full overflow-y-auto px-2.5 pt-2"
      >
        <Spin className="h-full! pt-8!" spinning={isLoading} tip={t('common.loadingActivities')}>
          <Timeline items={timelineItems} />
        </Spin>
      </div>
    </div>
  );
};

export default FileDetailActivitiesTab;
